import {
  ISupportingDocument,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import React, {useState} from 'react';
import IconButton from '@material-ui/core/IconButton';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {apiUploadFile} from 'constants/Api';

export function SupportingDocumentForm(props: {
  document?: Partial<ISupportingDocument>;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (document: ISupportingDocumentInput) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
  titleOptions?: string[];
}) {
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  type Submission = ISupportingDocumentInput;

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control} = useForm<Submission>({
    defaultValues: {
      ...props?.document,
    },
  });

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      id: submission.id,
      title: submission.title,
      url: submission.url,
    });
  };

  const uploadDocument = (event: any): Promise<string> | undefined => {
    if (props.readonly) return Promise.resolve('');
    const document = event?.target?.files?.[0];
    if (!document) return Promise.resolve('');

    const fileExtension = document.name.split('.').pop()?.substr(0, 4);
    const fileKey = `/${new Date().getTime()}.${fileExtension}`;

    setIsUploadingDocument(true);
    return apiUploadFile(document, fileKey)
      .then((url: string) => {
        // Return the URL to be set as the value
        setIsUploadingDocument(false);
        return url;
      })
      .catch((error) => {
        setIsUploadingDocument(false);
        alert('Error uploading document. Please try again later');
        console.error(error);
        throw error;
      });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container" style={{backgroundColor: 'white'}}>
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Controller
                name="title"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) =>
                  !props.titleOptions?.length ? (
                    <TextField
                      autoFocus
                      className="input"
                      label="Document Title"
                      variant="outlined"
                      fullWidth
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message || null}
                      disabled={props.readonly || props.isLoading}
                    />
                  ) : (
                    <FormControl
                      className="input"
                      fullWidth
                      error={!!error}
                      disabled={props.readonly || props.isLoading}
                      size="small"
                    >
                      <InputLabel id="document-title-select-label">
                        Document Title
                      </InputLabel>
                      <Select
                        labelId="document-title-select-label"
                        id="document-title-select"
                        value={value}
                        onChange={onChange}
                        fullWidth
                        variant="outlined"
                      >
                        {props.titleOptions.map((title) => (
                          <MenuItem key={title} value={title}>
                            {title}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{error?.message || null}</FormHelperText>
                    </FormControl>
                  )
                }
                rules={{
                  required: 'Document title required',
                }}
              />
            </Grid>
            <Grid item sm={12}>
              <Controller
                name="url"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => {
                  // Extract filename from URL for display, or use empty string to show placeholder
                  const displayValue = value
                    ? value.split('/').pop() || value
                    : '';

                  return (
                    <TextField
                      className="input"
                      label="Document"
                      variant="outlined"
                      fullWidth
                      value={displayValue || ''}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={true}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box display="flex" alignItems="center">
                              {value && (
                                <IconButton
                                  disabled={isUploadingDocument}
                                  href={value}
                                  target="_blank"
                                  title="Open Label Document"
                                  size="small"
                                  style={{marginRight: 4}}
                                >
                                  <OpenInNewIcon />
                                </IconButton>
                              )}
                              <Button
                                disabled={props.readonly || isUploadingDocument}
                                variant={'text'}
                                size="small"
                                component="label" // important for input upload to work
                              >
                                <input
                                  type="file"
                                  accept="**"
                                  hidden
                                  onChange={(event) => {
                                    const fileInput = event.target;
                                    if (fileInput.files && fileInput.files[0]) {
                                      uploadDocument(event)
                                        ?.then((url) => {
                                          // Ensure URL is set properly after upload completes
                                          if (url) {
                                            onChange(url);
                                          }
                                          // Reset the file input to allow selecting the same file again
                                          if (fileInput) {
                                            fileInput.value = '';
                                          }
                                        })
                                        .catch(() => {
                                          // Reset the file input on error
                                          if (fileInput) {
                                            fileInput.value = '';
                                          }
                                        });
                                    }
                                  }}
                                />
                                {isUploadingDocument ? (
                                  <CircularProgress
                                    color={'primary'}
                                    size={20}
                                    thickness={6}
                                    className={'avatar'}
                                  />
                                ) : (
                                  'Upload'
                                )}
                              </Button>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
                rules={{
                  required: 'Document required',
                }}
              />
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
